import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/setup/redux/store';
import { fetchUsers, setFilter, setLimit, setSortDirection, setStatus } from '../redux/userListSlice';
import { UserListFilter, UserListRequest } from '../types/UserList';

export function useUserList() {
  const dispatch = useDispatch<any>();
  const state = useSelector((root: RootState) => root.userList);
  const [cursor, setCursor] = useState<string | null>(null);
  const [history, setHistory] = useState<string[]>([]);

  const load = useCallback((requestCursor: string | null = null) => {
    const request: UserListRequest = {
      status: state.status,
      filter: state.filter,
      limit: state.limit,
      cursor: requestCursor,
      sort: state.sort,
    };
    return dispatch(fetchUsers(request));
  }, [dispatch, state.status, state.filter, state.limit, state.sort]);

  useEffect(() => {
    setCursor(null);
    setHistory([]);
    void load(null);
  }, [load]);

  const next = useCallback(() => {
    if (!state.nextCursor) return;
    setHistory((previous) => [...previous, cursor || '']);
    setCursor(state.nextCursor);
    void load(state.nextCursor);
  }, [cursor, load, state.nextCursor]);

  const previous = useCallback(() => {
    if (history.length === 0) return;
    const nextHistory = [...history];
    const previousCursor = nextHistory.pop() || null;
    setHistory(nextHistory);
    setCursor(previousCursor);
    void load(previousCursor);
  }, [history, load]);

  return {
    ...state,
    hasPrevious: history.length > 0,
    setStatus: (value: string[]) => dispatch(setStatus(value)),
    setFilter: (value: UserListFilter) => dispatch(setFilter(value)),
    setSortDirection: (value: 'asc' | 'desc') => dispatch(setSortDirection(value)),
    setLimit: (value: number) => dispatch(setLimit(value)),
    next,
    previous,
    reload: () => load(cursor),
  };
}

export default useUserList;
